<script setup lang="ts">
import { useRoute } from "vue-router";
import AppIcon from "./AppIcon.vue";
import ChuyLogo from "./ChuyLogo.vue";
import MenuDeSesion from "./MenuDeSesion.vue";
import UsageMeter from "./UsageMeter.vue";
import { useStatus } from "@/composables/useStatus";

/**
 * La barra de arriba: el logotipo, los tres lugares y el menú de la cuenta.
 *
 * En el teléfono los lugares se van a la barra de abajo y aquí quedan nomás el
 * logotipo y el menú.
 */
const route = useRoute();
const { pulso, online } = useStatus();

interface Lugar {
  to: string;
  label: string;
  icon: string;
  activo: (path: string) => boolean;
}

const LUGARES: Lugar[] = [
  { to: "/", label: "Tareas", icon: "list", activo: (p) => p === "/" || p.startsWith("/tareas") },
  { to: "/historial", label: "Historial", icon: "history", activo: (p) => p.startsWith("/historial") || p.startsWith("/corridas") },
  { to: "/ajustes", label: "Ajustes", icon: "settings", activo: (p) => p.startsWith("/ajustes") || p.startsWith("/usuarios") || p.startsWith("/bitacora") },
];
</script>

<template>
  <header class="pt-safe px-safe sticky top-0 z-30 border-b border-separator bg-[var(--sidebar)] backdrop-blur-xl">
    <div class="mx-auto flex h-14 w-full max-w-5xl items-center gap-6 px-4 sm:px-8">
      <RouterLink to="/" class="flex items-center gap-2.5 rounded-[10px]" aria-label="Chuy, a tus tareas">
        <ChuyLogo :size="28" />
        <span class="text-[16px] font-bold tracking-[-0.02em] text-content">Chuy</span>
      </RouterLink>

      <nav class="hidden items-center gap-1 md:flex" aria-label="Secciones">
        <RouterLink
          v-for="lugar in LUGARES"
          :key="lugar.to"
          :to="lugar.to"
          class="flex items-center gap-1.5 rounded-[9px] px-3 py-1.5 text-[13.5px] font-medium transition-colors"
          :class="lugar.activo(route.path) ? 'bg-surface text-content shadow-soft' : 'text-secondary hover:text-content'"
        >
          <AppIcon :name="lugar.icon" :size="15" />
          {{ lugar.label }}
        </RouterLink>
      </nav>

      <div class="ml-auto flex items-center gap-2 md:gap-3">
        <span
          v-if="!online"
          class="flex items-center gap-1.5 rounded-full bg-bad-soft px-2.5 py-1 text-[11.5px] font-semibold text-bad"
        >
          <AppIcon name="alert" :size="13" />
          Sin conexión
        </span>
        <span
          v-else-if="pulso && pulso.corriendo > 0"
          class="flex items-center gap-1.5 rounded-full bg-info-soft px-2.5 py-1 text-[11.5px] font-semibold text-accent"
          :title="`${pulso.corriendo} corriendo`"
        >
          <span class="animate-breathe size-1.5 rounded-full bg-current" />
          <span class="hidden sm:inline">{{ pulso.corriendo === 1 ? "1 tarea corriendo" : `${pulso.corriendo} corriendo` }}</span>
        </span>
        <UsageMeter />
        <MenuDeSesion />
      </div>
    </div>
  </header>
</template>
